import { useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Consolidates the three PDF operations (preview / download / print) so they
// share the same "save-if-dirty → fetch blob → dispatch" pipeline.
export function useDischargePdf({ patientId, hospitalId, dirty, persist }) {
  const _ensureSaved = useCallback(async () => {
    if (!dirty) return true;
    return await persist({ finalize: false });
  }, [dirty, persist]);

  const _fetchBlobUrl = useCallback(async () => {
    const resp = await api.get(`/discharge/${patientId}.pdf`, { responseType: "blob" });
    return URL.createObjectURL(resp.data);
  }, [patientId]);

  const openPdf = useCallback(async () => {
    if (!(await _ensureSaved())) return;
    try {
      const url = await _fetchBlobUrl();
      window.open(url, "_blank");
    } catch (e) {
      console.warn("PDF preview failed", e);
      toast.error("PDF generation failed");
    }
  }, [_ensureSaved, _fetchBlobUrl]);

  const printPdf = useCallback(async () => {
    if (!(await _ensureSaved())) return;
    try {
      const url = await _fetchBlobUrl();
      const w = window.open(url, "_blank");
      if (!w) return;
      const trigger = () => {
        try { w.focus(); w.print(); } catch (e) { console.warn("print() blocked", e); }
      };
      w.addEventListener?.("load", trigger);
      setTimeout(trigger, 1200);
    } catch (e) {
      console.warn("Print failed", e);
      toast.error("Print failed");
    }
  }, [_ensureSaved, _fetchBlobUrl]);

  const downloadPdf = useCallback(async () => {
    if (!(await _ensureSaved())) return;
    try {
      const url = await _fetchBlobUrl();
      const a = document.createElement("a");
      a.href = url;
      a.download = `discharge_${hospitalId || patientId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.warn("Download failed", e);
      toast.error("Download failed");
    }
  }, [_ensureSaved, _fetchBlobUrl, hospitalId, patientId]);

  return { openPdf, printPdf, downloadPdf };
}
